import { faArrowLeft, faGlobe, faServer } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link, useParams } from 'react-router-dom'
import { Page } from '~/components/Page'
import { useKillRaceInfo } from '../api/useKillRaceInfo'
import { Countdown } from '../components/Countdown'
import { KillRaceInfo } from '../components/KillRaceInfo'
import { KillRaceLeaderboard } from '../components/KillRaceLeaderboard'
import { ACCENT, Chip, ErrorBlock, Panel, PhaseBadge, Skeleton } from '../components/KillRaceUI'
import { formatDateTime, phaseOf, secondsUntil } from '../utils/killrace'

const bgDefault = new URL('../assets/killrace_default_banner.jpg', import.meta.url).href
const stopwatch = new URL('../assets/stopwatch_icon.svg', import.meta.url).href

export const KillRaceInfoPage = () => {
    const { uuid } = useParams()
    const { isError, refetch } = useKillRaceInfo(uuid)

    return (
        <Page title='Kill Race' header='Kill Race'>
            <div className='mx-auto flex w-full max-w-[1400px] flex-col gap-8 px-4 pb-24 pt-8'>
                <Link
                    to='/kill-race'
                    className='flex w-max items-center gap-2 text-2xs font-black uppercase tracking-[0.2em] text-white/40 transition-colors hover:text-white'
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    All kill races
                </Link>

                {/* If the race itself will not load there is nothing for the
                    panels below to describe, so say it once rather than
                    stacking a failure block per section. */}
                {isError ? (
                    <ErrorBlock title='Race not found' onRetry={refetch}>
                        This kill race no longer exists, or the link is wrong. It may have been removed after it
                        finished paying out.
                    </ErrorBlock>
                ) : (
                    <>
                        <RaceHeader uuid={uuid} />
                        <div className='grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]'>
                            <KillRaceInfo uuid={uuid} />
                            <KillRaceLeaderboard uuid={uuid} />
                        </div>
                    </>
                )}
            </div>
        </Page>
    )
}

/**
 * The banner used to be an `<img>` inside a `p-12 max-h-60 overflow-hidden`
 * box, which cropped it from the bottom at an arbitrary point and left a wide
 * empty gutter on either side. It is now a background on a fixed-ratio block,
 * so the artwork fills the header at every width and the text sits on it.
 */
const RaceHeader = ({ uuid }: { uuid: string }) => {
    const { data, isLoading, isError } = useKillRaceInfo(uuid)

    if (isLoading) return <Skeleton className='h-52 w-full md:h-64' />
    // The detail panel below already reports the failure with a retry; a second
    // error block stacked above it would just be noise.
    if (isError) return null

    const phase = phaseOf(data?.begin, data?.end)
    const isLive = phase === 'Live'
    const seconds = secondsUntil(isLive ? data?.end : data?.begin)
    const servers: string[] = data?.servers ?? []
    const banner = data?.banner || bgDefault

    return (
        <Panel size={24} border={ACCENT}>
            <div
                className='relative flex min-h-[13rem] flex-col justify-end gap-4 p-6 md:min-h-[16rem] md:p-8'
                style={{ backgroundImage: `url(${banner})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
            >
                <div className='absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/25' />

                <div className='relative flex flex-wrap items-center gap-2'>
                    <PhaseBadge phase={phase} />
                    <Chip icon={servers.length > 0 ? faServer : faGlobe}>{data?.mode ?? 'Global'}</Chip>
                    {servers.slice(0, 3).map((server) => (
                        <Chip key={server}>{server}</Chip>
                    ))}
                    {servers.length > 3 && <Chip>+{servers.length - 3}</Chip>}
                </div>

                <div className='relative flex flex-col gap-4 md:flex-row md:items-end md:justify-between'>
                    <div className='flex flex-col gap-2'>
                        <h1 className='text-2xl font-black uppercase leading-tight tracking-wide md:text-4xl'>
                            {data?.name ?? 'Kill Race'}
                        </h1>
                        <p className='text-2xs uppercase tracking-[0.18em] text-white/45'>
                            {formatDateTime(data?.begin)} to {formatDateTime(data?.end)}
                        </p>
                    </div>

                    <div className='flex shrink-0 items-center gap-3 border border-white/15 bg-black/50 px-4 py-3 backdrop-blur-sm'>
                        <img src={stopwatch} alt='' className='h-5 w-5 opacity-60' />
                        <div className='flex flex-col gap-1'>
                            <span className='text-2xs uppercase leading-none tracking-[0.2em] text-white/45'>
                                {isLive ? 'Ends in' : phase === 'Upcoming' ? 'Starts in' : 'Finished'}
                            </span>
                            <span className='text-lg font-black leading-none tabular-nums'>
                                {phase === 'Completed' || seconds === null ? (
                                    formatDateTime(data?.end)
                                ) : (
                                    <Countdown seconds={seconds} />
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </Panel>
    )
}
